/**
 * TeamReportView - Relatório de desempenho da equipe
 * 
 * Extraído do App.js durante refatoração
 * 
 * Funcionalidades:
 * - Métricas gerais da equipe (trabalhadores, produção, plantas)
 * - Gráfico de barras de produção por trabalhador
 * - Ranking do mês com medalhas
 * - Análise de eficiência (kg/planta)
 * - Análise de frequência (dias trabalhados)
 * - Análise de impacto de sangria perdida
 * - Opção de impressão
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card, CardHeader, CardTitle, CardContent, Button (shadcn/ui)
 */

import React, { useState } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "../translations";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { AproveitamentoPanel } from "./AproveitamentoPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamReportView = ({ property, month }) => {
  const { t } = useTranslation();
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const loadTeamReport = async () => {
    if (!property || !month) return;
    
    // Evitar múltiplas chamadas simultâneas
    if (loading) return;
    
    setLoading(true);
    
    // Timeout de segurança (10 segundos)
    const timeoutId = setTimeout(() => {
      setLoading(false);
      console.warn("Team Report - Timeout reached");
    }, 10000);
    
    try {
      // Buscar registros do mês atual
      const response = await axios.get(`${API}/bleeding-records/${property.id}?month=${month}`);
      const records = response.data || [];
      
      // Determinar início da safra (Set do ano anterior se mês atual é Jan-Ago, senão Set do ano atual)
      const [year, monthNum] = month.split('-').map(Number);
      const harvestStartYear = monthNum >= 9 ? year : year - 1;
      const harvestStart = `${harvestStartYear}-09`;
      
      // Buscar TODOS os registros desde o início da safra até o mês atual
      // para calcular cortes corretamente
      let allHarvestRecords = records;
      if (month > harvestStart) {
        try {
          const harvestResponse = await axios.get(`${API}/bleeding-records/${property.id}/harvest-records?start=${harvestStart}&end=${month}`);
          allHarvestRecords = harvestResponse.data || records;
          console.log("Team Report - Harvest records loaded:", allHarvestRecords.length);
        } catch (e) {
          // Se o endpoint não existe, usar apenas os registros do mês
          console.log("Team Report - Using month records only");
          allHarvestRecords = records;
        }
      }

      // Buscar team-report-v2 para obter sangradores_alocacao (plantas individuais por trabalhador).
      // Falha silenciosa: se o endpoint não responder ou propriedade não tiver safra_configs,
      // o relatório segue usando a divisão igualitária / record.plantas_talhao como fallback.
      let teamV2 = null;
      try {
        const v2Response = await axios.get(`${API}/bleeding-records/${property.id}/team-report-v2`, {
          params: { month }
        });
        teamV2 = v2Response.data || null;
      } catch (e) {
        teamV2 = null;
      }

      console.log("Team Report - Records loaded:", records.length);
      
      // Processar dados para criar relatório da equipe
      // Passa os registros do mês E todos os registros da safra para cálculo de cortes
      const teamStats = processTeamData(records, allHarvestRecords, teamV2);
      setTeamData(teamStats);
      setShowReport(true);
      
      console.log("Team Report - Data processed:", teamStats);
    } catch (error) {
      console.error("Error loading team report:", error);
      // Mostrar dados vazios em caso de erro
      setTeamData({ workers: [], totalProduction: 0, totalPlants: 0, totalRecords: 0, avgPerPlant: 0 });
      setShowReport(true);
    } finally {
      // Limpar timeout e resetar loading
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  const processTeamData = (monthRecords, allHarvestRecords = null, teamV2 = null) => {
    // Se não passou registros da safra, usar apenas os do mês
    const harvestRecords = allHarvestRecords || monthRecords;
    const records = monthRecords; // Registros do mês para produção/plantas
    
    const workers = {};
    let totalProduction = 0;

    // Contar sangrias por lote-dia (mesma régua do painel Aproveitamento: dia com 2 lotes = 2 sangrias)
    const lotesPorDiaMes = {};
    let totalReposicoesMes = 0;

    records.forEach(record => {
      if (record.sangrias && record.sangrias.length > 0) {
        record.sangrias.forEach(s => {
          if (s.lote) (lotesPorDiaMes[record.date] = lotesPorDiaMes[record.date] || new Set()).add(s.lote);
        });
      } else if (record.sangria) {
        (lotesPorDiaMes[record.date] = lotesPorDiaMes[record.date] || new Set()).add(record.talhao_lote_sangria || '_dia');
      }
      totalReposicoesMes += record.reposicao || 0;
    });
    const totalSangriasMes = Object.values(lotesPorDiaMes).reduce((acc, s) => acc + s.size, 0);
    const diasSangriaMes = Object.keys(lotesPorDiaMes).length;
    const diasDuplosMes = Object.values(lotesPorDiaMes).filter(s => s.size >= 2).length;

    // RÉGUA OFICIAL: 1 dia de sangria = 1 corte (mesmo com 2 lotes) + reposições.
    // Mesma régua do Resumo Mensal. Lote extra de dia duplo = crédito no Aproveitamento.
    const totalCortesMes = diasSangriaMes + totalReposicoesMes;

    // Ordenar registros do mês por data
    const sortedMonthRecords = [...records].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // Ordenar TODOS os registros da safra por data (para cálculo de cortes)
    const sortedHarvestRecords = [...harvestRecords].sort((a, b) => new Date(a.date) - new Date(b.date));
    
    /**
     * LÓGICA CORRETA DE CÁLCULO POR LOTE:
     * 1. Agrupar SANGRIAS por LOTE (usando talhao_lote_sangria)
     * 2. Agrupar COLETAS por LOTE (histórico de coletas de cada lote)
     * 3. Para cada lote coletado no mês, encontrar a COLETA ANTERIOR desse lote
     * 4. Contar sangrias do lote entre coleta anterior e coleta atual
     * 5. kg/planta/corte = total_producao / total_plantas / soma_cortes_todos_lotes
     */
    
    // Função para normalizar nome do lote
    const normalizarLote = (lote) => {
      if (!lote) return null;
      const loteLower = lote.toLowerCase().trim();
      if (loteLower.includes('1') || loteLower === '1') return 'Lot 1';
      if (loteLower.includes('2') || loteLower === '2') return 'Lot 2';
      if (loteLower.includes('3') || loteLower === '3') return 'Lot 3';
      if (loteLower.includes('4') || loteLower === '4') return 'Lot 4';
      return lote;
    };
    
    // Agrupar SANGRIAS por LOTE (usando array sangrias — valores fracionários)
    const sangriasPorLote = {};
    // Agrupar REPOSIÇÕES por LOTE (valores fracionários - soma dos valores)
    const reposicoesPorLote = {};
    
    sortedHarvestRecords.forEach(record => {
      // Contabilizar sangrias a partir do array sangrias (cada entrada tem lote e valor)
      if (record.sangrias && record.sangrias.length > 0) {
        record.sangrias.forEach(s => {
          const lote = normalizarLote(s.lote);
          if (lote) {
            if (!sangriasPorLote[lote]) {
              sangriasPorLote[lote] = [];
            }
            sangriasPorLote[lote].push({
              date: record.date,
              valor: s.valor || 1.0
            });
          }
        });
      } else if (record.sangria && record.talhao_lote_sangria) {
        // Backward compat: registros antigos sem array sangrias
        const lote = normalizarLote(record.talhao_lote_sangria);
        if (lote) {
          if (!sangriasPorLote[lote]) {
            sangriasPorLote[lote] = [];
          }
          sangriasPorLote[lote].push({
            date: record.date,
            valor: 1.0
          });
        }
      }
      
      // Contabilizar REPOSIÇÕES (valor fracionário por data)
      // Ex: 0.5 no dia 18 + 0.5 no dia 19 = 1 corte total
      if (record.reposicao && record.reposicao > 0 && record.talhao_lote_reposicao) {
        const loteReposicao = normalizarLote(record.talhao_lote_reposicao);
        if (loteReposicao) {
          if (!reposicoesPorLote[loteReposicao]) {
            reposicoesPorLote[loteReposicao] = [];
          }
          reposicoesPorLote[loteReposicao].push({
            date: record.date,
            valor: record.reposicao
          });
        }
      }
    });
    
    // Ordenar sangrias por data
    Object.keys(sangriasPorLote).forEach(lote => {
      sangriasPorLote[lote].sort((a, b) => a.date.localeCompare(b.date));
    });
    
    // Ordenar reposições por data
    Object.keys(reposicoesPorLote).forEach(lote => {
      reposicoesPorLote[lote].sort((a, b) => a.date.localeCompare(b.date));
    });
    
    // Agrupar COLETAS por LOTE (histórico)
    // IMPORTANTE: excluímos coletas feitas em dias de ENTREGA (producao_kg > 0).
    // Esses são "fechamentos" que consolidam sangrias/reposições anteriores e
    // NÃO iniciam um novo sub-ciclo. Se fossem incluídos, sangrias/reposições
    // do dia da entrega ficariam órfãs entre os intervalos.
    const datasEntregaProducao = new Set(
      sortedHarvestRecords.filter(r => r.producao_kg > 0).map(r => r.date)
    );
    const coletasPorLote = {};
    sortedHarvestRecords.forEach(record => {
      if (record.coleta_realizada && record.lotes_coletados && record.lotes_coletados.length > 0) {
        // Ignora "coleta de fechamento" (mesmo dia da entrega)
        if (datasEntregaProducao.has(record.date)) return;
        record.lotes_coletados.forEach(loteOriginal => {
          const lote = normalizarLote(loteOriginal);
          if (lote) {
            if (!coletasPorLote[lote]) {
              coletasPorLote[lote] = [];
            }
            if (!coletasPorLote[lote].includes(record.date)) {
              coletasPorLote[lote].push(record.date);
            }
          }
        });
      }
    });
    
    // Ordenar coletas por lote
    Object.keys(coletasPorLote).forEach(lote => {
      coletasPorLote[lote].sort();
    });
    
    // Identificar entregas do mês
    const entregasComProducao = sortedMonthRecords.filter(r => r.producao_kg > 0);
    const datasEntregasMes = [...new Set(entregasComProducao.map(e => e.date))].sort();
    
    // Função para somar sangrias de um lote em um período (retorna {total, detalhes})
    // Regra confirmada com o usuário: sangria feita APÓS a coleta do mesmo lote
    // (inclusive no mesmo dia da coleta) vai pra PRÓXIMA entrega.
    // Range: [dataInicio, dataFim) — INCLUSIVO no início, EXCLUSIVO no fim.
    //   - Sangria no dia da coleta anterior (dataInicio) = APÓS aquela coleta = esta entrega
    //   - Sangria no dia da coleta atual (dataFim) = APÓS esta coleta = próxima entrega
    const somarSangriasDoLote = (lote, dataInicio, dataFim) => {
      const sangrias = sangriasPorLote[lote] || [];
      const sangriasNoPeriodo = sangrias.filter(s => {
        if (dataInicio && s.date < dataInicio) return false;
        if (s.date >= dataFim) return false;
        return true;
      });
      const total = sangriasNoPeriodo.reduce((sum, s) => sum + s.valor, 0);
      return {
        total,
        detalhes: sangriasNoPeriodo.map(s => `${s.date} (${s.valor})`)
      };
    };
    
    // Função para somar reposições de um lote em um período (retorna {total, detalhes})
    // Mesma regra: [dataInicio, dataFim) inclusivo no início, exclusivo no fim.
    const somarReposicoesDoLote = (lote, dataInicio, dataFim) => {
      const reposicoes = reposicoesPorLote[lote] || [];
      const reposicoesNoPeriodo = reposicoes.filter(r => {
        if (dataInicio && r.date < dataInicio) return false;
        if (r.date >= dataFim) return false;
        return true;
      });
      const total = reposicoesNoPeriodo.reduce((sum, r) => sum + r.valor, 0);
      return {
        total,
        detalhes: reposicoesNoPeriodo.map(r => `${r.date} (${r.valor})`)
      };
    };
    
    // Função para encontrar a coleta anterior de um lote
    const encontrarColetaAnterior = (lote, dataColetaAtual) => {
      const coletas = coletasPorLote[lote] || [];
      const index = coletas.indexOf(dataColetaAtual);
      if (index > 0) {
        return coletas[index - 1];
      }
      return null; // Primeira coleta do lote
    };
    
    // Para cada data de entrega do MÊS, calcular cortes por lote
    let totalCortesPorLote = 0;
    let totalColetas = 0;
    const detalhesCalculoPorEntrega = [];
    
    // Identificar TODAS as entregas da SAFRA (para saber qual foi a entrega anterior)
    const todasEntregasSafra = [...new Set(
      sortedHarvestRecords
        .filter(r => r.producao_kg > 0)
        .map(r => r.date)
    )].sort();
    
    datasEntregasMes.forEach(dataEntrega => {
      // Encontrar a entrega anterior NA SAFRA (não apenas no mês)
      const indexNaSafra = todasEntregasSafra.indexOf(dataEntrega);
      const entregaAnteriorSafra = indexNaSafra > 0 ? todasEntregasSafra[indexNaSafra - 1] : null;
      
      // Buscar coletas entre a entrega anterior (da safra) e esta entrega
      const coletasNovas = sortedHarvestRecords.filter(r => 
        r.coleta_realizada && 
        r.lotes_coletados && 
        r.lotes_coletados.length > 0 &&
        r.date < dataEntrega &&
        (!entregaAnteriorSafra || r.date > entregaAnteriorSafra)
      );
      
      if (coletasNovas.length > 0) {
        totalColetas += coletasNovas.length;
        
        let cortesDestaEntrega = 0;
        const detalheLotes = [];
        
        // Para cada coleta, calcular cortes de cada lote
        coletasNovas.forEach(coleta => {
          (coleta.lotes_coletados || []).forEach(loteOriginal => {
            const lote = normalizarLote(loteOriginal);
            if (!lote) return;
            
            // Encontrar a coleta ANTERIOR deste lote específico
            const coletaAnterior = encontrarColetaAnterior(lote, coleta.date);
            
            // Contar sangrias do lote entre coleta anterior e coleta atual (agora com valores fracionários)
            const sangriasNoPeriodo = somarSangriasDoLote(lote, coletaAnterior, coleta.date);
            const numSangrias = sangriasNoPeriodo.total;
            
            // Somar reposições do lote no mesmo período
            const reposicoesNoPeriodo = somarReposicoesDoLote(lote, coletaAnterior, coleta.date);
            const numReposicoes = reposicoesNoPeriodo.total;
            
            // Total de cortes = sangrias + reposições
            const cortes = numSangrias + numReposicoes;
            
            cortesDestaEntrega += cortes;
            detalheLotes.push({ 
              lote, 
              dataColeta: coleta.date, 
              coletaAnterior, 
              sangrias: sangriasNoPeriodo.detalhes,
              reposicoes: reposicoesNoPeriodo.detalhes,
              numReposicoes,
              numSangrias,
              cortes 
            });
          });
        });
        
        totalCortesPorLote += cortesDestaEntrega;
        
        detalhesCalculoPorEntrega.push({
          dataEntrega,
          coletas: coletasNovas.length,
          cortesTotal: cortesDestaEntrega,
          detalhes: detalheLotes
        });
        
        // Debug log
        console.log(`📦 Entrega ${dataEntrega}:`);
        detalheLotes.forEach(d => {
          console.log(`   ${d.lote}: coleta ${d.dataColeta} (anterior: ${d.coletaAnterior || 'nenhuma'}) - ${d.cortes} cortes`);
          console.log(`      Sangrias: ${d.sangrias.join(', ')}`);
        });
        console.log(`   Total cortes desta entrega: ${cortesDestaEntrega}`);
      }
    });
    
    console.log(`📊 TOTAL CORTES (soma por lote): ${totalCortesPorLote}`);
    
    // Guardar detalhes do cálculo para debug
    const calculoAvancado = {
      totalCortesPorLote,
      detalhes: detalhesCalculoPorEntrega,
      usouCalculoPorLote: totalCortesPorLote > 0,
      sangriasPorLote,
      coletasPorLote
    };

    // Agrupar dados por trabalhador (do mês)
    records.forEach(record => {
      if (record.trabalhador_responsavel) {
        const worker = record.trabalhador_responsavel;
        if (!workers[worker]) {
          workers[worker] = {
            name: worker,
            production: 0,
            plants: record.plantas_talhao || 0,
            entregas: 0,
            records: [],
            coletas: []
          };
        }
        
        workers[worker].production += record.producao_kg || 0;
        if (workers[worker].plants === 0 && record.plantas_talhao > 0) {
          workers[worker].plants = record.plantas_talhao;
        }
        if (record.producao_kg > 0) {
          workers[worker].entregas += 1;
          // Adicionar entrega como coleta (com vinculações se houver)
          workers[worker].coletas.push(record);
        }
        workers[worker].records.push(record);
        
        totalProduction += record.producao_kg || 0;
      }
    });
    
    // Se não há trabalhadores mas há registros gerais, criar resumo geral
    if (Object.keys(workers).length === 0 && records.length > 0) {
      // Processar como dados gerais da propriedade
      const generalProduction = records.reduce((sum, r) => sum + (r.producao_kg || 0), 0);
      const generalPlants = records.reduce((max, r) => Math.max(max, r.plantas_talhao || 0), 0);
      
      if (generalProduction > 0) {
        workers['Geral'] = {
          name: 'Geral',
          production: generalProduction,
          plants: generalPlants,
          entregas: entregasComProducao.length,
          records: records,
          coletas: entregasComProducao
        };
        totalProduction = generalProduction;
      }
    }

    // Calcular total de plantas
    const totalPlants = Object.values(workers).reduce((sum, w) => sum + (w.plants || 0), 0);

    // MÉTODO PLANTAS-CORTE:
    // 🥈 por_lote: produção ÷ (plantas/lote × cortes por lote vinculados às entregas do mês)
    // 🥉 estimativa: fallback legado (produção ÷ plantas totais ÷ dias de sangria do mês)
    const lotesConhecidos = new Set([...Object.keys(sangriasPorLote), ...Object.keys(coletasPorLote)]);
    const numLotes = lotesConhecidos.size;
    const plantasPorLote = numLotes > 0 && totalPlants > 0 ? totalPlants / numLotes : 0;
    const usaCalculoPorLote = totalCortesPorLote > 0 && plantasPorLote > 0;
    // 🥇 Nível preciso: backend calculou plantas-corte com lotes DESIGUAIS (lotes_config).
    // Consumimos o valor autoritativo do backend p/ garantir consistência total entre telas.
    // Quando NÃO há lotes_config, este bloco fica inativo e tudo segue como 🥈/🥉 (inalterado).
    const plantasCortePreciso = !!(teamV2 && teamV2.plantas_corte_metodo === 'por_lote' && teamV2.plantas_corte_total > 0);
    const plantasCorteTotalV2 = plantasCortePreciso ? teamV2.plantas_corte_total : 0;
    const totalPlantasV2 = teamV2 ? (teamV2.total_plantas_propriedade || 0) : 0;
    const numWorkersAtivos = Object.keys(workers).length;

    // Mapa de plantas individuais vindo da nova alocação por safra (sangradores_alocacao).
    // Quando disponível (teamV2 retornou plantas_individuais por trabalhador), este é o valor
    // AUTORITATIVO — mais preciso que record.plantas_talhao e que a divisão igualitária.
    const plantasAlocadasPorNome = {};
    if (teamV2 && Array.isArray(teamV2.sangradores)) {
      teamV2.sangradores.forEach(s => {
        if (s && s.nome && s.plantas_individuais != null) {
          plantasAlocadasPorNome[s.nome.trim().toLowerCase()] = s.plantas_individuais;
        }
      });
    }

    // Converter para array e calcular métricas por trabalhador
    const workersArray = Object.values(workers).map(worker => {
      // Plantas atribuíveis a este worker (ordem de prioridade):
      //  1) Alocação detalhada via sangradores_alocacao (teamV2) — autoritativo
      //  2) record.plantas_talhao (worker.plants) — quando preenchido
      //  3) Divisão igualitária do total entre workers ativos — fallback
      const plantasAloc = plantasAlocadasPorNome[(worker.name || '').trim().toLowerCase()];
      let plantasWorker;
      let plantasOrigem;
      if (plantasAloc && plantasAloc > 0) {
        plantasWorker = plantasAloc;
        plantasOrigem = 'alocacao';
      } else if (worker.plants > 0) {
        plantasWorker = worker.plants;
        plantasOrigem = 'talhao';
      } else if (totalPlants > 0 && numWorkersAtivos > 0) {
        plantasWorker = Math.round(totalPlants / numWorkersAtivos);
        plantasOrigem = 'igualitaria';
      } else {
        plantasWorker = 0;
        plantasOrigem = 'indisponivel';
      }

      // kg/planta/corte individual (plantas-corte):
      // 🥇 por_lote_preciso: rateio do denominador da propriedade (lotes desiguais)
      // 🥈 por_lote: plantas do worker ÷ nº lotes × cortes por lote (lotes iguais)
      let workerAvgPerPlantPerCut = 0;
      if (plantasCortePreciso && plantasWorker > 0 && totalPlantasV2 > 0) {
        const denom = (plantasWorker / totalPlantasV2) * plantasCorteTotalV2;
        workerAvgPerPlantPerCut = denom > 0 ? (worker.production / denom).toFixed(4) : 0;
      } else if (usaCalculoPorLote && plantasWorker > 0) {
        workerAvgPerPlantPerCut = (worker.production / ((plantasWorker / numLotes) * totalCortesPorLote)).toFixed(4);
      } else if (plantasWorker > 0 && totalCortesMes > 0) {
        workerAvgPerPlantPerCut = (worker.production / plantasWorker / totalCortesMes).toFixed(4);
      }

      return {
        ...worker,
        plantsCalculated: plantasWorker,
        plantsOrigem: plantasOrigem,
        avgPerPlant: plantasWorker > 0 ? (worker.production / plantasWorker).toFixed(4) : 0,
        avgPerEntrega: worker.entregas > 0 ? (worker.production / worker.entregas).toFixed(2) : 0,
        avgPerPlantPerCut: workerAvgPerPlantPerCut
      };
    });

    // Ordenar por produção total
    workersArray.sort((a, b) => b.production - a.production);

    // Calcular total de cortes da SAFRA (para exibição quando tem coletas vinculadas)
    const diasComSangriaSafra = new Set();
    let totalReposicoesSafra = 0;
    harvestRecords.forEach(record => {
      if (record.sangria) {
        diasComSangriaSafra.add(record.date);
      }
      totalReposicoesSafra += record.reposicao || 0;
    });
    const totalCortesSafra = diasComSangriaSafra.size + totalReposicoesSafra;
    
    // CÁLCULO DO kg/planta/corte (TOTAL DA EQUIPE) — método plantas-corte:
    // 🥇 por_lote_preciso: lotes desiguais (via lotes_config) — valor autoritativo do backend.
    // 🥈 por_lote: lotes iguais (produção ÷ (plantas/lote × cortes por lote das entregas)).
    // 🥉 estimativa: fallback legado quando não há coletas com lotes registradas.
    let avgPerPlantPerCut = 0;
    let calcMethod = 'estimativa';
    if (plantasCortePreciso) {
      calcMethod = 'por_lote_preciso';
      avgPerPlantPerCut = (totalProduction / plantasCorteTotalV2).toFixed(4);
      console.log(`ℹ️ kg/planta/corte 🥇 = ${totalProduction}kg ÷ ${plantasCorteTotalV2} plantas-corte (lotes desiguais) = ${avgPerPlantPerCut}`);
    } else if (usaCalculoPorLote) {
      calcMethod = 'por_lote';
      avgPerPlantPerCut = (totalProduction / (plantasPorLote * totalCortesPorLote)).toFixed(4);
      console.log(`ℹ️ kg/planta/corte = ${totalProduction}kg ÷ (${plantasPorLote} plantas/lote × ${totalCortesPorLote} cortes das entregas) = ${avgPerPlantPerCut}`);
    } else if (totalPlants > 0 && totalCortesMes > 0) {
      avgPerPlantPerCut = (totalProduction / totalPlants / totalCortesMes).toFixed(4);
      console.log(`ℹ️ kg/planta/corte (estimativa) = ${totalProduction}kg / ${totalPlants} plantas / ${totalCortesMes} cortes = ${avgPerPlantPerCut}`);
    }

    return {
      workers: workersArray,
      totalProduction,
      totalPlants,
      totalRecords: records.length,
      totalSangrias: totalSangriasMes,
      totalReposicoes: totalReposicoesMes,
      totalCortes: totalCortesMes,
      diasSangriaMes,
      diasDuplosMes,
      totalCortesSafra, // Cortes desde início da safra
      totalColetas, // Total de coletas (vinculações automáticas + diretas)
      totalCortesPorLote: calculoAvancado.totalCortesPorLote, // Soma de cortes de todos os lotes
      detalhesCalculoPorLote: calculoAvancado.detalhes || [], // Detalhes por entrega e lote
      avgPerPlant: totalPlants > 0 ? (totalProduction / totalPlants).toFixed(4) : 0,
      avgPerPlantPerCut,
      calcMethod,
      numLotes,
      plantasPorLote: Math.round(plantasPorLote),
      plantasCorteTotal: plantasCorteTotalV2 || (usaCalculoPorLote ? Math.round(plantasPorLote * totalCortesPorLote) : 0)
    };
  };

  const generateChart = () => {
    if (!teamData || teamData.workers.length === 0) return null;

    const maxProduction = Math.max(...teamData.workers.map(w => w.production));
    
    return (
      <div className="space-y-3">
        <h4 className="font-medium text-gray-800">📊 Produção por Trabalhador (kg)</h4>
        {teamData.workers.map((worker, index) => {
          const percentage = maxProduction > 0 ? (worker.production / maxProduction) * 100 : 0;
          const colors = ['bg-blue-500', 'bg-orange-500', 'bg-gray-500', 'bg-yellow-500'];
          const color = colors[index] || 'bg-purple-500';
          
          return (
            <div key={worker.name} className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="font-medium">{worker.name}</span>
                <span className="text-gray-600">{worker.production.toFixed(1)}kg</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6 relative">
                <div 
                  className={`${color} h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-2`}
                  style={{ width: `${Math.max(percentage, 5)}%` }}
                >
                  <span className="text-white text-xs font-medium">{worker.production.toFixed(1)}</span>
                </div>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{t('trpPlants')}: {worker.plantsCalculated || worker.plants}</span>
                <span>{t('trpDeliveries')}: {worker.entregas}</span>
                <span>kg/planta/corte: {parseFloat(worker.avgPerPlantPerCut) > 0 ? worker.avgPerPlantPerCut : '—'}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (!property) return null;

  return (
    <Card className="mb-6 border-green-200 bg-green-50">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <CardTitle className="flex items-center text-green-800">
            📊 {t('teamReport')}
          </CardTitle>
          <Button 
            onClick={loadTeamReport} 
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
            data-testid="generate-team-report-btn"
          >
            {loading ? t('loading') : `🤝 ${t('generateTeamReport') || 'Gerar Relatório da Equipe'}`}
          </Button>
        </div>
      </CardHeader>
      
      {showReport && teamData && (
        <CardContent className="space-y-6">
          <div className="bg-white p-4 rounded-lg border">
            <h3 className="font-semibold text-green-800 mb-4">📈 Resumo da Equipe - {month}</h3>
            
            {/* Métricas Gerais - Linha Única */}
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 mb-6">
              <div className="text-center p-2 bg-blue-50 rounded-lg">
                <div className="text-xl font-bold text-blue-600">{teamData.workers.length}</div>
                <div className="text-xs text-gray-600">{t('trpWorkersLabel')}</div>
              </div>
              <div className="text-center p-2 bg-green-50 rounded-lg">
                <div className="text-xl font-bold text-green-600">{teamData.totalProduction.toFixed(1)}kg</div>
                <div className="text-xs text-gray-600">{t('trpTotalProd')}</div>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded-lg">
                <div className="text-xl font-bold text-orange-600">{teamData.totalPlants}</div>
                <div className="text-xs text-gray-600">{t('trpTotalPlants')}</div>
              </div>
              <div className="text-center p-2 bg-indigo-50 rounded-lg">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-help">
                        <div className="text-xl font-bold text-indigo-600" data-testid="card-cortes-mes">
                          {teamData.totalCortes || 0}
                        </div>
                        <div className="text-xs text-gray-600 underline decoration-dotted">
                          Cortes (Mês)
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-white text-gray-800 p-2 rounded-lg shadow-xl border border-gray-200 z-50">
                      <p className="text-xs">
                        <strong>{teamData.diasSangriaMes || 0}</strong> dia(s) de sangria + <strong>{teamData.totalReposicoes || 0}</strong> reposições = <strong>{teamData.totalCortes || 0}</strong> cortes — mesma régua do Resumo Mensal
                        {teamData.diasDuplosMes > 0 && (
                          <><br /><span className="text-gray-500">{teamData.diasDuplosMes} dia(s) com 2 lotes sangrados: o lote extra não soma aqui — vira crédito de recuperação (+{teamData.diasDuplosMes}) no painel Aproveitamento & Perdas.</span></>
                        )}
                        {teamData.calcMethod === 'por_lote' && (
                          <><br /><span className="text-gray-500">O kg/planta/corte usa os <strong>{teamData.totalCortesPorLote}</strong> cortes por lote vinculados às entregas do mês (ver detalhes).</span></>
                        )}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-center p-2 bg-cyan-50 rounded-lg">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-help">
                        <div className="text-xl font-bold text-cyan-600">{teamData.totalColetas || 0}</div>
                        <div className="text-xs text-gray-600 underline decoration-dotted">{t('trpCollections')}</div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-white text-gray-800 p-2 rounded-lg shadow-xl border border-gray-200 z-50">
                      <p className="text-xs">{t('trpCollectionsWithProd')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-center p-2 bg-amber-50 rounded-lg">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="cursor-help">
                        <div className="text-xl font-bold text-amber-600">{teamData.avgPerPlant || 0}</div>
                        <div className="text-xs text-gray-600 underline decoration-dotted">kg/planta</div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-white text-gray-800 p-2 rounded-lg shadow-xl border border-gray-200 z-50">
                      <p className="text-xs">{t('trpAccumulated')}: {teamData.totalProduction?.toFixed(1) || 0}kg ÷ {teamData.totalPlants || 0} {t('trpPlants2')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="text-center p-2 bg-purple-50 rounded-lg">
                <div className="text-xl font-bold text-purple-600" data-testid="card-kg-planta-corte">{teamData.avgPerPlantPerCut || 0}</div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-xs text-gray-600 cursor-help underline decoration-dotted">
                        kg/planta/corte {teamData.calcMethod === 'por_lote_preciso' ? '🥇' : teamData.calcMethod === 'por_lote' ? '🥈' : '🥉'}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-white text-gray-800 p-2 rounded-lg shadow-xl border border-gray-200 z-50">
                      {teamData.calcMethod === 'por_lote_preciso' ? (
                        <p className="text-xs">
                          {teamData.totalProduction?.toFixed(1) || 0}kg ÷ {teamData.plantasCorteTotal} plantas-corte = <strong>{teamData.avgPerPlantPerCut}</strong>
                          <br /><span className="text-gray-500">🥇 Cálculo preciso por lote — usa as plantas reais de cada lote (lotes desiguais configurados) × os cortes que geraram as entregas do mês</span>
                        </p>
                      ) : teamData.calcMethod === 'por_lote' ? (
                        <p className="text-xs">
                          {teamData.totalProduction?.toFixed(1) || 0}kg ÷ ({teamData.plantasPorLote} plantas/lote × {teamData.totalCortesPorLote} cortes) = <strong>{teamData.avgPerPlantPerCut}</strong>
                          <br /><span className="text-gray-500">🥈 Cálculo por lote — usa apenas os cortes que geraram as entregas do mês ({teamData.numLotes} lotes, plantas divididas igualmente). Configure plantas por lote na Safra para o nível 🥇.</span>
                          <br /><span className="text-gray-500">≠ dos {teamData.totalCortes || 0} cortes oficiais do mês: aqui cada lote conta separado (dia com 2 lotes = 2) e a janela segue as entregas, não o calendário.</span>
                        </p>
                      ) : (
                        <p className="text-xs">
                          {teamData.totalProduction?.toFixed(1) || 0}kg ÷ {teamData.totalPlants || 0} plantas ÷ {teamData.totalCortes || 0} cortes
                          <br /><span className="text-gray-500">🥉 Estimativa simplificada — registre coletas com lotes para o cálculo preciso</span>
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            
            {/* Botão Ver Detalhes de Cortes por Lote - Sempre visível */}
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="mt-3 text-indigo-600 border-indigo-300 hover:bg-indigo-50" data-testid="ver-detalhes-cortes-btn">
                  📋 Ver Detalhes de Cortes por Lote
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-gray-800">
                    📊 Detalhes de Cortes por Lote - {month}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {teamData.detalhesCalculoPorLote && teamData.detalhesCalculoPorLote.length > 0 ? (
                    <>
                      {teamData.detalhesCalculoPorLote.map((entrega, idx) => (
                        <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                          <div className="font-medium text-green-700 mb-2">
                            📦 Entrega: {entrega.dataEntrega}
                          </div>
                          <div className="overflow-x-auto">
                          <table className="w-full text-sm min-w-[420px]">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left py-1 px-2">{t('trpLot')}</th>
                                <th className="text-left py-1 px-2">{t('trpCollectionDate')}</th>
                                <th className="text-left py-1 px-2">{t('trpPreviousCollection')}</th>
                                <th className="text-center py-1 px-2">{t('cpCuts')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entrega.detalhes && entrega.detalhes.map((detalhe, dIdx) => (
                                <tr key={dIdx} className="border-b last:border-b-0">
                                  <td className="py-1 px-2 font-medium text-indigo-600">{detalhe.lote}</td>
                                  <td className="py-1 px-2">{detalhe.dataColeta}</td>
                                  <td className="py-1 px-2 text-gray-500">{detalhe.coletaAnterior || '—'}</td>
                                  <td className="py-1 px-2 text-center font-bold text-green-600">{detalhe.cortes}</td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="bg-indigo-100">
                                <td colSpan="3" className="py-1 px-2 font-medium text-right">{t('trpTotalThisDelivery')}:</td>
                                <td className="py-1 px-2 text-center font-bold text-indigo-700">{entrega.cortesTotal}</td>
                              </tr>
                            </tfoot>
                          </table>
                          </div>
                          {/* Mostrar sangrias e reposições de cada lote */}
                          <div className="mt-2 text-xs text-gray-600">
                            <div className="font-medium mb-1">📅 Detalhes dos cortes por lote:</div>
                            {entrega.detalhes && entrega.detalhes.map((detalhe, dIdx) => (
                              <div key={dIdx} className="ml-2 mb-1">
                                <span className="font-medium">{detalhe.lote}:</span>
                                {detalhe.sangrias?.length > 0 && (
                                  <span className="text-green-600"> Sangrias: {detalhe.sangrias.join(', ')}</span>
                                )}
                                {detalhe.reposicoes?.length > 0 && (
                                  <span className="text-orange-600"> | Reposições: {detalhe.reposicoes.join(', ')}</span>
                                )}
                                {(!detalhe.sangrias?.length && !detalhe.reposicoes?.length) && ' —'}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      
                      {/* Resumo Total */}
                      <div className="border-t-2 border-indigo-300 pt-3 mt-4">
                        <div className="flex justify-between items-center text-lg font-bold">
                          <span>Cortes vinculados às entregas:</span>
                          <span className="text-indigo-700" data-testid="total-cortes-por-lote">{teamData.totalCortesPorLote}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm text-gray-600">
                          <span>{t('trpMonthTotalCuts')} (calendário):</span>
                          <span>{teamData.totalCortes}</span>
                        </div>
                        {teamData.calcMethod === 'por_lote' ? (
                          <div className="text-sm text-gray-600 mt-1">
                            kg/planta/corte = {teamData.totalProduction?.toFixed(1)}kg ÷ ({teamData.plantasPorLote} plantas/lote × {teamData.totalCortesPorLote} cortes) = <strong>{teamData.avgPerPlantPerCut}</strong>
                          </div>
                        ) : (
                          <div className="text-sm text-gray-600 mt-1">
                            kg/planta/corte (estimativa) = {teamData.totalProduction?.toFixed(1)}kg ÷ {teamData.totalPlants} plantas ÷ {teamData.totalCortes} cortes = <strong>{teamData.avgPerPlantPerCut}</strong>
                          </div>
                        )}
                        {teamData.calcMethod === 'por_lote' && (
                          <div className="text-xs text-gray-500 mt-2 italic">
                            ℹ️ Fórmula plantas-corte: usa os cortes que geraram as entregas do mês e as plantas de cada lote ({teamData.plantasPorLote}/lote), refletindo o rodízio real do seringal.
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-6">
                      <div className="text-amber-600 bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <p className="font-medium mb-2">ℹ️ Não há detalhes de cortes por lote para este mês</p>
                        <p className="text-sm text-gray-600">
                          Nenhuma coleta com lotes especificados foi encontrada neste período.
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Para ver detalhes, registre coletas indicando os lotes específicos (Lot 1, Lot 2, etc.) antes da entrega.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Gráfico de Barras */}
            {generateChart()}

            {/* Ranking da Equipe */}
            {teamData.workers.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-800 mb-3">🏆 Ranking do Mês</h4>
                <div className="space-y-2">
                  {teamData.workers.map((worker, index) => (
                    <div key={worker.name} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                          index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-500' : 'bg-gray-300'
                        }`}>
                          {index + 1}
                        </div>
                        <span className="font-medium">{worker.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-green-600">{worker.production.toFixed(1)}kg</div>
                        <div className="text-xs text-gray-500">{worker.days} dias • {worker.avgPerDay}kg/dia</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Painel Aproveitamento & Perdas (visão do proprietário) — sempre visível no relatório */}
            <AproveitamentoPanel property={property} month={month} />

            {/* NOVA SEÇÃO: Análise de Performance */}
            {teamData.workers.length > 0 && (
              <div className="mt-8 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-4 flex items-center">
                  🔍 Análise de Performance
                </h4>
                
                {/* Resumo de Cortes do Mês */}
                <div className="mb-6 p-3 bg-white rounded-lg border">
                  <h5 className="font-medium text-gray-700 mb-2">📅 Cortes do Mês</h5>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="text-blue-600"><strong>{teamData.totalSangrias || 0}</strong> sangrias</span>
                    <span className="text-green-600"><strong>+</strong></span>
                    <span className="text-orange-600"><strong>{teamData.totalReposicoes || 0}</strong> reposições</span>
                    <span className="text-gray-600"><strong>=</strong></span>
                    <span className="text-purple-600 font-bold"><strong>{teamData.totalCortes || 0}</strong> cortes totais</span>
                  </div>
                </div>

                {/* Análise de Eficiência */}
                <div className="mb-6">
                  <h5 className="font-medium text-gray-700 mb-2">📊 Eficiência (kg/planta/corte)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {teamData.workers.slice(0, 4).map((worker, index) => {
                      const v = parseFloat(worker.avgPerPlantPerCut) || 0;
                      return (
                        <div key={worker.name} className="flex justify-between items-center p-2 bg-white rounded border" data-testid={`worker-eficiencia-${index}`}>
                          <span className="text-sm font-medium">{worker.name}</span>
                          <span className="text-sm text-blue-600 font-semibold">
                            {v > 0 ? `${worker.avgPerPlantPerCut} kg/planta/corte` : '— sem dados de plantas'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Análise de Entregas */}
                <div className="mb-6">
                  <h5 className="font-medium text-gray-700 mb-2">📦 Entregas Realizadas</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {teamData.workers.slice(0, 4).map((worker, index) => (
                      <div key={worker.name} className="flex justify-between items-center p-2 bg-white rounded border">
                        <span className="text-sm font-medium">{worker.name}</span>
                        <span className="text-sm text-green-600 font-semibold">{worker.entregas} entregas | {worker.avgPerEntrega}kg/entrega</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Análise de Sangria Perdida */}
                <div className="mb-6">
                  <h5 className="font-medium text-gray-700 mb-2">⚠️ Impacto Sangria Perdida</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {teamData.workers.slice(0, 4).map((worker, index) => {
                      const lostBleedings = worker.records.reduce((sum, record) => sum + (record.sangria_perdida || 0), 0);
                      return (
                        <div key={worker.name} className="flex justify-between items-center p-2 bg-white rounded border">
                          <span className="text-sm font-medium">{worker.name}</span>
                          <span className={`text-sm font-semibold ${lostBleedings > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {lostBleedings} perdidas
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Disclaimer Equilibrado */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <div className="text-yellow-600 text-lg">ℹ️</div>
                    <div className="text-xs text-yellow-800">
                      <p className="font-semibold mb-1">📋 Insights Equilibrados</p>
                      <p className="leading-relaxed">
                        Esta análise considera apenas os dados registrados. O desempenho real pode ser influenciado por diversos fatores externos como:
                        <span className="font-medium"> condições climáticas, tipo de clone das plantas, saúde do seringal, qualidade do solo, época do ano</span> e outros aspectos técnicos.
                        Use estas informações como referência, sempre considerando o contexto completo da operação.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Botões de Ação do Relatório */}
                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowReport(false);
                      setTeamData({ workers: [], totalProduction: 0, totalPlants: 0, totalRecords: 0, avgPerPlant: 0 });
                    }}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    ❌ {t('closeReport')}
                  </button>
                  
                  <button
                    onClick={() => {
                      window.print();
                    }}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    🖨️ {t('printReport')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default TeamReportView;
